import { useEffect, useRef, useState } from "react";

const products = [
  {
    name: "CUB VPN",
    tagline: "Secure. Private. Fast.",
    description:
      "Military-grade encryption that shields your internet traffic from prying eyes. Browse freely, access content globally, and stay protected on any network — all with blazing-fast speeds.",
    category: "Privacy & Security",
    highlights: ["256-bit AES Encryption", "No-Log Policy", "Global Server Network", "Kill Switch Protection"],
    image:
      "https://public.readdy.ai/ai/img_res/337a2f2a45bdb133043a911eebfb6912.jpg",
    accent: "from-emerald-500 to-teal-600",
    iconClass: "ri-shield-keyhole-line",
  },
  {
    name: "CUB Cleaner",
    tagline: "Clean. Optimize. Breathe.",
    description:
      "Reclaim your iPhone&apos;s full potential. Our intelligent cleaner removes junk files, duplicate photos, and unnecessary caches — giving your device the performance boost it deserves.",
    category: "Performance & Optimization",
    highlights: ["Smart Junk Detection", "Duplicate Photo Finder", "Cache Cleaner", "Storage Analyzer"],
    image:
      "https://readdy.ai/api/search-image?query=modern%20phone%20cleaner%20iOS%20app%20interface%20mockup%20on%20iPhone%2C%20clean%20white%20design%20with%20storage%20optimization%20charts%20and%20progress%20indicators%2C%20light%20mint%20and%20white%20color%20scheme%2C%20minimal%20UI%20design%2C%20professional%20app%20screenshot%2C%20soft%20light%20background%2C%20high%20quality%20render&width=400&height=500&seq=product-cleaner-001&orientation=portrait",
    accent: "from-sky-500 to-indigo-600",
    iconClass: "ri-sparkling-line",
  },
];

export default function ProductsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="products"
      ref={sectionRef}
      className="w-full py-24 md:py-36"
      style={{ background: "linear-gradient(180deg, #0d1117 0%, #161b22 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`mb-16 md:mb-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-emerald-400 mb-4">Our Applications</p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-2xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Products That Enhance
            <span className="block text-white/40 font-light">Your Mobile Experience</span>
          </h2>
        </div>

        {/* Product Cards */}
        <div className="flex flex-col gap-12 md:gap-16">
          {products.map((product, i) => (
            <div
              key={product.name}
              className={`flex flex-col ${i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-10 lg:gap-16 items-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${200 + i * 200}ms` }}
            >
              {/* Image */}
              <div className="w-full lg:w-2/5 flex justify-center">
                <div className="relative">
                  <div className={`absolute -inset-4 rounded-3xl bg-gradient-to-br ${product.accent} opacity-20 blur-2xl`} />
                  <div className="relative w-56 md:w-72 rounded-3xl overflow-hidden border border-white/10">
                    <img
                      src={product.image}
                      alt={`${product.name} app interface`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-3/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${product.accent}`}>
                    <i className={`${product.iconClass} text-white text-lg`} />
                  </div>
                  <span className="text-xs font-semibold tracking-widest uppercase text-white/40">{product.category}</span>
                </div>

                <h3
                  className="text-3xl md:text-4xl font-bold text-white mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {product.name}
                </h3>
                <p className={`text-base font-medium bg-gradient-to-r ${product.accent} bg-clip-text text-transparent mb-5`}>
                  {product.tagline}
                </p>
                <p className="text-white/60 text-base leading-relaxed mb-8 max-w-lg">
                  {product.name === "CUB VPN"
                    ? "Military-grade encryption that shields your internet traffic from prying eyes. Browse freely, access content globally, and stay protected on any network — all with blazing-fast speeds."
                    : "Reclaim your iPhone's full potential. Our intelligent cleaner removes junk files, duplicate photos, and unnecessary caches — giving your device the performance boost it deserves."}
                </p>

                {/* Highlights */}
                <div className="grid grid-cols-2 gap-3">
                  {product.highlights.map((h) => (
                    <div key={h} className="flex items-center gap-2">
                      <div className={`w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-br ${product.accent} flex-shrink-0`}>
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                      <span className="text-white/70 text-sm">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
